import apiClient from './config';
import { handleApiError, buildQueryString } from './utils';
import { getCurrentLanguage } from '../config/languageConfig';
import { createCacheKey, getCacheData, setCacheData } from './cacheUtils';

// 배송 주소 생성 요청 인터페이스
export interface CreateAddressRequest {
  address: string;
  detail_address: string;
  lat: string;
  lng: string;
}

// 배송 주소 응답 인터페이스
export interface AddressResponse {
  id: string;
  address: string;
  detail_address: string;
  lat: string;
  lng: string;
  created_at: string;
  updated_at: string;
}

// Stay 정보 인터페이스
export interface StayInfo {
  id: string;
  stay_name: string;
  stay_id: string;
  created_date: string;
  updated_date: string;
  display_info?: string; // 뷰모드에서 표시할 주소 정보
}

// 확장된 배송 주소 응답 인터페이스 (Stay 정보 포함)
export interface EnhancedAddressResponse {
  id: string;
  address: string;
  detailAddress: string;
  lat: number;
  lng: number;
  addressKr: string;
  detailAddressKr: string;
  createdDate: string;
  updatedDate: string;
  stay?: StayInfo;
  roadAddress?: string | null;
  jibunAddress?: string | null;
  roadAddressEn?: string | null;
  jibunAddressEn?: string | null;
}

/**
 * 배송 주소 생성 API
 * @param data 배송 주소 생성 요청 데이터
 * @returns 생성된 배송 주소 정보
 */
export const createAddress = async (data: CreateAddressRequest): Promise<AddressResponse> => {
  try {
    // 현재 설정된 언어 추가
    const currentLang = getCurrentLanguage();
    const queryString = buildQueryString({ lang: currentLang });
    
    const response = await apiClient.post<AddressResponse>(`/api/delivery-addresses${queryString}`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 배송 주소 조회 API
 * @param addressId 배송 주소 ID
 * @returns 배송 주소 정보
 */
export const getAddress = async (addressId: string): Promise<AddressResponse> => {
  try {
    // 현재 설정된 언어 가져오기
    const currentLang = getCurrentLanguage();
    
    // 엔드포인트 및 파라미터
    const endpoint = `/api/delivery-addresses/${addressId}`;
    const params = { lang: currentLang };
    
    // 캐시 키 생성
    const cacheKey = createCacheKey(endpoint, params);
    
    // 캐시된 데이터 확인
    const cachedData = getCacheData<AddressResponse>(cacheKey, currentLang);
    if (cachedData) {
      return cachedData;
    }
    
    // 쿼리 파라미터 생성
    const queryString = buildQueryString(params);
    
    const response = await apiClient.get<AddressResponse>(`${endpoint}${queryString}`);
    // 응답 데이터 캐싱
    setCacheData(cacheKey, response.data, currentLang);
    
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 배송 주소 목록 조회 API
 * @returns 배송 주소 목록
 */
export const getAddresses = async (): Promise<AddressResponse[]> => {
  try {
    // 현재 설정된 언어 가져오기
    const currentLang = getCurrentLanguage();
    
    // 엔드포인트 및 파라미터
    const endpoint = '/api/delivery-addresses';
    const params = { lang: currentLang };
    
    // 캐시 키 생성
    const cacheKey = createCacheKey(endpoint, params);
    
    // 캐시된 데이터 확인
    const cachedData = getCacheData<AddressResponse[]>(cacheKey, currentLang);
    if (cachedData) {
      return cachedData;
    }
    
    // 쿼리 파라미터 생성
    const queryString = buildQueryString(params);
    
    const response = await apiClient.get<AddressResponse[]>(`${endpoint}${queryString}`);
    // 응답 데이터 캐싱
    setCacheData(cacheKey, response.data, currentLang);
    
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 확장된 배송 주소 조회 API (Stay 정보 포함)
 * @param addressId 배송 주소 ID
 * @returns Stay 정보를 포함한 배송 주소 정보
 */
export const getEnhancedAddress = async (addressId: string): Promise<EnhancedAddressResponse> => {
  try {
    const currentLang = getCurrentLanguage();
    const endpoint = `/api/delivery-addresses/${addressId}`;
    const cacheKey = createCacheKey(endpoint, { enhanced: true });

    const cachedData = getCacheData<EnhancedAddressResponse>(cacheKey, currentLang);
    if (cachedData) {
      return cachedData;
    }

    const response = await apiClient.get<EnhancedAddressResponse>(endpoint);
    setCacheData(cacheKey, response.data, currentLang);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
